import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { http, imageUrl } from "@/lib/api";
import { PAGE_SCHEMAS } from "@/lib/pageSchemas";
import { toast, Toaster } from "sonner";
import { Plus, X, Edit2, Trash2, Upload, LogOut, Settings as SettingsIcon, Tag, FileText } from "lucide-react";

const EMPTY_WATCH = {
  name: "",
  collection: "",
  movement: "",
  movement_slug: "",
  crystal: "",
  case_size: "",
  water_resistance: "",
  bracelet: "",
  price: "",
  pricing_mode: "fixed",
  description: "",
  images: [],
  category_slugs: [],
  is_featured: false,
  is_new_arrival: false,
};

const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const { settings, reload: reloadSettings } = useSettings();
  const navigate = useNavigate();
  const [tab, setTab] = useState("watches");

  const [watches, setWatches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [movements, setMovements] = useState([]);
  const [editing, setEditing] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", slug: "" });
  const [mvForm, setMvForm] = useState({ name: "", slug: "", group_slug: "" });
  const [mvGroups, setMvGroups] = useState([]);
  const [mvGroupForm, setMvGroupForm] = useState({ name: "", slug: "" });
  const [editingTaxon, setEditingTaxon] = useState(null);
  const [contentPage, setContentPage] = useState(PAGE_SCHEMAS[0].key);
  const [contentForm, setContentForm] = useState({});

  const loadAll = async () => {
    const [w, c, m, g] = await Promise.all([http.get("/watches"), http.get("/categories"), http.get("/movements"), http.get("/movement-groups")]);
    setWatches(w.data);
    setCategories(c.data);
    setMovements(m.data);
    setMvGroups(g.data);
  };

  useEffect(() => {
    if (user) loadAll();
  }, [user]);

  useEffect(() => {
    if (settings) setSettingsForm(settings);
  }, [settings]);

  useEffect(() => {
    if (user && tab === "content") {
      http.get(`/page-content/${contentPage}`).then((r) => setContentForm(r.data || {}));
    }
  }, [user, tab, contentPage]);

  if (loading) return <div className="p-12 text-[var(--aq-muted)]">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const openNew = () => setEditing({ ...EMPTY_WATCH });
  const openEdit = (w) => setEditing({ ...EMPTY_WATCH, ...w });
  const closeEditor = () => setEditing(null);

  const saveWatch = async () => {
    if (!editing.name || !editing.collection) {
      toast.error("Nombre y colección son obligatorios");
      return;
    }
    try {
      if (editing.id) {
        await http.put(`/watches/${editing.id}`, editing);
        toast.success("Reloj actualizado");
      } else {
        await http.post("/watches", editing);
        toast.success("Reloj creado");
      }
      await loadAll();
      closeEditor();
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  const deleteWatch = async (id) => {
    if (!window.confirm("¿Eliminar este reloj?")) return;
    await http.delete(`/watches/${id}`);
    toast.success("Reloj eliminado");
    loadAll();
  };

  const uploadFiles = async (files) => {
    const uploaded = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await http.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.path);
      } catch (e) {
        toast.error(`No se pudo subir ${f.name}`);
      }
    }
    if (uploaded.length) {
      setEditing((prev) => ({ ...prev, images: [...(prev.images || []), ...uploaded] }));
      toast.success(`${uploaded.length} imagen(es) subida(s)`);
    }
  };

  const removeImage = (idx) => {
    setEditing((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const toggleCat = (slug) => {
    setEditing((prev) => {
      const has = prev.category_slugs.includes(slug);
      return {
        ...prev,
        category_slugs: has ? prev.category_slugs.filter((s) => s !== slug) : [...prev.category_slugs, slug],
      };
    });
  };

  const createCategory = async () => {
    if (!catForm.name.trim()) return;
    try {
      await http.post("/categories", catForm);
      setCatForm({ name: "", slug: "" });
      loadAll();
      toast.success("Categoría agregada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo agregar la categoría");
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("¿Eliminar esta categoría?")) return;
    await http.delete(`/categories/${id}`);
    loadAll();
  };

  const saveSettings = async () => {
    try {
      await http.put("/settings", settingsForm);
      await reloadSettings();
      toast.success("Ajustes guardados");
    } catch (e) {
      toast.error("No se pudieron guardar los ajustes");
    }
  };

  // single image upload helper → returns stored path
  const uploadSingle = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await http.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.path;
  };

  const saveTaxon = async () => {
    if (!editingTaxon?.name?.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    try {
      await http.put(`/${editingTaxon.endpoint}/${editingTaxon.id}`, {
        name: editingTaxon.name,
        description: editingTaxon.description || "",
        image_url: editingTaxon.image_url || "",
        sort_order: editingTaxon.sort_order || 0,
        group_slug: editingTaxon.group_slug || "",
      });
      toast.success("Guardado");
      setEditingTaxon(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    }
  };

  const saveContent = async () => {
    try {
      await http.put(`/page-content/${contentPage}`, contentForm);
      toast.success("Contenido guardado");
    } catch (e) {
      toast.error("No se pudo guardar el contenido");
    }
  };

  const uploadContentImage = async (fieldKey, file) => {
    try {
      const path = await uploadSingle(file);
      setContentForm((prev) => ({ ...prev, [fieldKey]: path }));
      toast.success("Imagen subida");
    } catch (e) {
      toast.error("No se pudo subir la imagen");
    }
  };

  const currentSchema = PAGE_SCHEMAS.find((p) => p.key === contentPage);

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-12">
      <Toaster richColors position="top-center" />
      <div className="flex items-center justify-between mb-12">
        <div>
          <p className="overline mb-2">Consola Administrador</p>
          <h1 className="font-display text-3xl sm:text-4xl">AQ Watches</h1>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-xs uppercase tracking-[0.24em] text-[var(--aq-muted)] hover:text-black">Ver Sitio</Link>
          <button onClick={onLogout} data-testid="admin-logout" className="btn-outline">
            <LogOut size={14} /> Cerrar Sesión
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="flex border-b border-[var(--aq-border)] mb-10">
        {[
          { k: "watches", label: "Watches", icon: <Tag size={14} /> },
          { k: "categories", label: "Categories", icon: <Tag size={14} /> },
          { k: "content", label: "Contenido", icon: <FileText size={14} /> },
          { k: "settings", label: "Ajustes", icon: <SettingsIcon size={14} /> },
          { k: "movements", label: "Movimientos", icon: <Tag size={14} /> },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admin-tab-${t.k}`}
            className={`px-6 py-4 text-[11px] uppercase tracking-[0.28em] border-b-2 transition ${
              tab === t.k ? "border-black text-black" : "border-transparent text-[var(--aq-muted)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* WATCHES TAB */}
      {tab === "watches" && (
        <div>
          <div className="flex items-center justify-between mb-8">
            <p className="text-sm text-[var(--aq-muted)]">{watches.length} relojes</p>
            <button onClick={openNew} data-testid="admin-new-watch" className="btn-primary">
              <Plus size={14} /> Nuevo Reloj
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {watches.map((w) => (
              <div key={w.id} className="border border-[var(--aq-border)] p-5 flex gap-5">
                <div className="w-24 h-32 bg-[var(--aq-surface)] flex-shrink-0 overflow-hidden">
                  {w.images?.[0] && <img src={imageUrl(w.images[0])} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {w.is_featured && <span className="text-[10px] uppercase tracking-widest bg-black text-white px-2 py-0.5">Destacado</span>}
                    {w.is_new_arrival && <span className="text-[10px] uppercase tracking-widest border border-black px-2 py-0.5">Nuevo</span>}
                  </div>
                  <h3 className="font-display text-lg leading-tight">{w.name}</h3>
                  <p className="text-xs text-[var(--aq-muted)] mb-2">{w.collection} · {w.movement || "—"}</p>
                  <p className="text-sm">{w.price}</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => openEdit(w)} data-testid={`admin-edit-${w.id}`} className="text-xs uppercase tracking-widest border-b border-black">
                      Editar
                    </button>
                    <button onClick={() => deleteWatch(w.id)} data-testid={`admin-delete-${w.id}`} className="text-xs uppercase tracking-widest border-b border-red-600 text-red-600">
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CATEGORIES TAB */}
      {tab === "categories" && (
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl mb-6">Categorías</h2>
          <ul className="space-y-2 mb-10">
            {categories.map((c) => (
              <li key={c.id} className="flex items-center justify-between border border-[var(--aq-border)] px-5 py-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 bg-[var(--aq-surface)] flex-shrink-0 overflow-hidden">
                    {c.image_url && <img src={imageUrl(c.image_url)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{c.name}</p>
                    <p className="text-xs text-[var(--aq-muted)]">{c.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <button onClick={() => setEditingTaxon({ ...c, endpoint: "categories", kind: "category" })} data-testid={`edit-cat-${c.slug}`} className="text-[var(--aq-ink)]">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => deleteCategory(c.id)} data-testid={`del-cat-${c.slug}`} className="text-red-600">
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <h3 className="font-display text-lg mb-3">Agregar categoría</h3>
          <div className="flex gap-3">
            <input className="aq-input flex-1" placeholder="Nombre (ej. Relojes Aviador)" value={catForm.name}
              onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="new-cat-name" />
            <input className="aq-input flex-1" placeholder="slug-opcional" value={catForm.slug}
              onChange={(e) => setCatForm({ ...catForm, slug: e.target.value })} data-testid="new-cat-slug" />
            <button onClick={createCategory} data-testid="new-cat-submit" className="btn-primary">Agregar</button>
          </div>
        </div>
      )}

      {/* CONTENT TAB */}
      {tab === "content" && currentSchema && (
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl mb-6">Contenido de Páginas</h2>
          <div className="flex flex-wrap gap-2 mb-8">
            {PAGE_SCHEMAS.map((p) => (
              <button
                key={p.key}
                onClick={() => setContentPage(p.key)}
                data-testid={`content-page-${p.key}`}
                className={`text-xs uppercase tracking-[0.2em] px-4 py-2 border ${
                  contentPage === p.key ? "bg-black text-white border-black" : "border-[var(--aq-border)] text-[var(--aq-muted)]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            {Object.keys(contentForm).length === 0 ? (
              <p className="text-sm text-[var(--aq-muted)]">Cargando contenido…</p>
            ) : currentSchema.fields.map((f) => (
              <div key={f.key}>
                <label className="overline block mb-2 mt-6">{f.label}</label>
                {f.type === "textarea" && (
                  <textarea
                    className="aq-input min-h-[90px]"
                    value={contentForm[f.key] || ""}
                    onChange={(e) => setContentForm({ ...contentForm, [f.key]: e.target.value })}
                    data-testid={`content-field-${f.key}`}
                  />
                )}
                {f.type === "text" && (
                  <input
                    className="aq-input"
                    value={contentForm[f.key] || ""}
                    onChange={(e) => setContentForm({ ...contentForm, [f.key]: e.target.value })}
                    data-testid={`content-field-${f.key}`}
                  />
                )}
                {f.type === "image" && (
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-[var(--aq-surface)] overflow-hidden flex-shrink-0">
                      {contentForm[f.key] && <img src={imageUrl(contentForm[f.key])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        className="aq-input"
                        placeholder="URL de imagen o sube un archivo"
                        value={contentForm[f.key] || ""}
                        onChange={(e) => setContentForm({ ...contentForm, [f.key]: e.target.value })}
                        data-testid={`content-field-${f.key}`}
                      />
                      <label className="inline-flex items-center gap-2 cursor-pointer btn-outline">
                        <Upload size={14} /> Subir
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadContentImage(f.key, e.target.files[0])}
                          data-testid={`content-upload-${f.key}`}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="pt-8">
            <button onClick={saveContent} disabled={Object.keys(contentForm).length === 0} data-testid="content-save" className="btn-primary disabled:opacity-50">Guardar Contenido</button>
          </div>
        </div>
      )}


      {/* MOVEMENTS TAB */}
      {tab === "movements" && (
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl mb-6">Movimientos</h2>
          <ul className="space-y-2 mb-10">
            {movements.map((m) => (
              <li key={m.id} className="flex items-center justify-between border border-[var(--aq-border)] px-5 py-4">
                <div>
                  <p className="text-sm">{m.name}</p>
                  <p className="text-xs text-[var(--aq-muted)]">{m.slug}</p>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm("¿Eliminar este movimiento?")) return;
                    await http.delete(`/movements/${m.id}`);
                    loadAll();
                  }}
                  data-testid={`del-mv-${m.slug}`}
                  className="text-red-600"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
          <h3 className="font-display text-lg mb-3">Agregar movimiento</h3>
          <div className="flex flex-wrap gap-3 mb-8">
            <input className="aq-input flex-1 min-w-[140px]" placeholder="Nombre (ej. NH35)" value={mvForm.name}
              onChange={(e) => setMvForm({ ...mvForm, name: e.target.value })} data-testid="new-mv-name" />
            <select className="aq-input bg-white flex-1 min-w-[140px]" value={mvForm.group_slug}
              onChange={(e) => setMvForm({ ...mvForm, group_slug: e.target.value })} data-testid="new-mv-group">
              <option value="">— Grupo —</option>
              {mvGroups.map((g) => <option key={g.id} value={g.slug}>{g.name}</option>)}
            </select>
            <button onClick={async () => {
              if (!mvForm.name.trim()) return;
              try {
                await http.post("/movements", mvForm);
                setMvForm({ name: "", slug: "", group_slug: "" });
                loadAll();
                toast.success("Movimiento agregado");
              } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
            }} data-testid="new-mv-submit" className="btn-primary">Agregar</button>
          </div>

          <h3 className="font-display text-lg mb-3 mt-10 pt-6 border-t border-[var(--aq-border)]">Grupos de Movimientos</h3>
          <ul className="space-y-2 mb-6">
            {mvGroups.map((g) => (
              <li key={g.id} className="flex items-center justify-between border border-[var(--aq-border)] px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 bg-[var(--aq-surface)] flex-shrink-0 overflow-hidden">
                    {g.image_url && <img src={imageUrl(g.image_url)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <span className="text-sm truncate">{g.name} <span className="text-[var(--aq-muted)] text-xs">({g.slug})</span></span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <button onClick={() => setEditingTaxon({ ...g, endpoint: "movement-groups", kind: "group" })} data-testid={`edit-mvg-${g.slug}`} className="text-[var(--aq-ink)]">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={async () => {
                    if (!window.confirm("¿Eliminar este grupo?")) return;
                    await http.delete(`/movement-groups/${g.id}`); loadAll();
                  }} className="text-red-600" data-testid={`del-mvg-${g.slug}`}><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex gap-3">
            <input className="aq-input flex-1" placeholder="Nombre del grupo (ej. Suizos)" value={mvGroupForm.name}
              onChange={(e) => setMvGroupForm({ ...mvGroupForm, name: e.target.value })} data-testid="new-mvg-name" />
            <button onClick={async () => {
              if (!mvGroupForm.name.trim()) return;
              try {
                await http.post("/movement-groups", mvGroupForm);
                setMvGroupForm({ name: "", slug: "" }); loadAll();
                toast.success("Grupo agregado");
              } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
            }} data-testid="new-mvg-submit" className="btn-primary">Agregar Grupo</button>
          </div>
        </div>
      )}

      {/* SETTINGS TAB */}
      {tab === "settings" && settingsForm && (
        <div className="max-w-2xl space-y-2">
          <h2 className="font-display text-2xl mb-4">Ajustes del Sitio</h2>

          <label className="overline block mb-2 mt-6">Número de WhatsApp (con código de país)</label>
          <input className="aq-input" value={settingsForm.whatsapp_number || ""}
            onChange={(e) => setSettingsForm({ ...settingsForm, whatsapp_number: e.target.value })}
            data-testid="settings-whatsapp" placeholder="+52 555 000 0000" />

          <label className="overline block mb-2 mt-6">Usuario de Instagram</label>
          <input className="aq-input" value={settingsForm.instagram_handle || ""}
            onChange={(e) => setSettingsForm({ ...settingsForm, instagram_handle: e.target.value })}
            data-testid="settings-instagram-handle" placeholder="@aqwatchvault" />

          <label className="overline block mb-2 mt-6">URL de Instagram</label>
          <input className="aq-input" value={settingsForm.instagram_url || ""}
            onChange={(e) => setSettingsForm({ ...settingsForm, instagram_url: e.target.value })}
            data-testid="settings-instagram-url" placeholder="https://instagram.com/aqwatchvault" />

          <label className="overline block mb-2 mt-6">Título del Hero</label>
          <input className="aq-input" value={settingsForm.hero_headline || ""}
            onChange={(e) => setSettingsForm({ ...settingsForm, hero_headline: e.target.value })}
            data-testid="settings-hero-headline" />

          <label className="overline block mb-2 mt-6">Subtítulo del Hero</label>
          <input className="aq-input" value={settingsForm.hero_subheadline || ""}
            onChange={(e) => setSettingsForm({ ...settingsForm, hero_subheadline: e.target.value })} />

          <label className="overline block mb-2 mt-6">URL de Imagen Hero</label>
          <input className="aq-input" value={settingsForm.hero_image || ""}
            onChange={(e) => setSettingsForm({ ...settingsForm, hero_image: e.target.value })} />

          <div className="pt-8">
            <button onClick={saveSettings} data-testid="settings-save" className="btn-primary">Guardar Ajustes</button>
          </div>
        </div>
      )}

      {/* EDITOR DRAWER */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={closeEditor}>
          <div
            className="bg-white w-full max-w-2xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="watch-editor"
          >
            <div className="sticky top-0 bg-white border-b border-[var(--aq-border)] px-8 py-6 flex items-center justify-between">
              <h3 className="font-display text-2xl">{editing.id ? "Editar Reloj" : "Nuevo Reloj"}</h3>
              <button onClick={closeEditor}><X size={22} /></button>
            </div>
            <div className="p-8 space-y-2">
              <label className="overline block mb-2">Nombre</label>
              <input className="aq-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="editor-name" />

              <label className="overline block mb-2 mt-6">Colección</label>
              <input className="aq-input" value={editing.collection} onChange={(e) => setEditing({ ...editing, collection: e.target.value })} data-testid="editor-collection" />

              <div className="grid grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="overline block mb-2">Movimiento</label>
                  <select
                    className="aq-input bg-white"
                    value={editing.movement_slug || ""}
                    onChange={(e) => {
                      const slug = e.target.value;
                      const mv = movements.find((m) => m.slug === slug);
                      setEditing({ ...editing, movement_slug: slug, movement: mv ? mv.name : "" });
                    }}
                    data-testid="editor-movement-slug"
                  >
                    <option value="">— Selecciona —</option>
                    {movements.map((m) => (
                      <option key={m.id} value={m.slug}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">Cristal</label>
                  <input className="aq-input" value={editing.crystal} onChange={(e) => setEditing({ ...editing, crystal: e.target.value })} />
                </div>
                <div>
                  <label className="overline block mb-2">Tamaño de Caja</label>
                  <input className="aq-input" value={editing.case_size} onChange={(e) => setEditing({ ...editing, case_size: e.target.value })} />
                </div>
                <div>
                  <label className="overline block mb-2">Resistencia al Agua</label>
                  <input className="aq-input" value={editing.water_resistance} onChange={(e) => setEditing({ ...editing, water_resistance: e.target.value })} />
                </div>
                <div>
                  <label className="overline block mb-2">Pulsera</label>
                  <input className="aq-input" value={editing.bracelet} onChange={(e) => setEditing({ ...editing, bracelet: e.target.value })} />
                </div>
                <div>
                  <label className="overline block mb-2">Modo de Precio</label>
                  <select
                    className="aq-input bg-white"
                    value={editing.pricing_mode || "fixed"}
                    onChange={(e) => setEditing({ ...editing, pricing_mode: e.target.value })}
                    data-testid="editor-pricing-mode"
                  >
                    <option value="fixed">Precio fijo</option>
                    <option value="quote">Solicitar cotización</option>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">Precio (RD$)</label>
                  <input
                    className="aq-input"
                    value={editing.price}
                    onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                    data-testid="editor-price"
                    placeholder="RD$12,000"
                    disabled={editing.pricing_mode === "quote"}
                  />
                </div>
              </div>

              <label className="overline block mb-2 mt-6">Descripción</label>
              <textarea className="aq-input min-h-[100px]" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />

              <label className="overline block mb-3 mt-8">Categorías</label>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCat(c.slug)}
                    className={`text-xs uppercase tracking-widest px-3 py-1.5 border ${
                      editing.category_slugs.includes(c.slug) ? "bg-black text-white border-black" : "border-[var(--aq-border)]"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>

              <div className="flex gap-6 mt-8">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" className="aq-checkbox" checked={editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} data-testid="editor-featured" />
                  <span className="text-sm">Destacado</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" className="aq-checkbox" checked={editing.is_new_arrival} onChange={(e) => setEditing({ ...editing, is_new_arrival: e.target.checked })} data-testid="editor-new-arrival" />
                  <span className="text-sm">Nuevo Ingreso</span>
                </label>
              </div>

              <label className="overline block mb-3 mt-8">Imágenes</label>
              <div className="grid grid-cols-4 gap-3 mb-3">
                {editing.images?.map((img, i) => (
                  <div key={i} className="relative aspect-square bg-[var(--aq-surface)] overflow-hidden">
                    <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-white/90 p-1">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer btn-outline">
                <Upload size={14} /> Subir Imágenes
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
                  data-testid="editor-upload"
                />
              </label>
              <p className="text-xs text-[var(--aq-muted)] mt-2">O pega URLs de imágenes (una por línea):</p>
              <textarea
                className="aq-input min-h-[60px]"
                placeholder="https://..."
                onBlur={(e) => {
                  const urls = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean);
                  if (urls.length) {
                    setEditing((prev) => ({ ...prev, images: [...(prev.images || []), ...urls] }));
                    e.target.value = "";
                  }
                }}
              />

              <div className="pt-10 flex gap-4">
                <button onClick={saveWatch} data-testid="editor-save" className="btn-primary flex-1">Guardar Reloj</button>
                <button onClick={closeEditor} className="btn-outline">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAXON EDITOR DRAWER (categorías + grupos de movimientos) */}
      {editingTaxon && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setEditingTaxon(null)}>
          <div
            className="bg-white w-full max-w-md h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="category-editor"
          >
            <div className="sticky top-0 bg-white border-b border-[var(--aq-border)] px-8 py-6 flex items-center justify-between">
              <h3 className="font-display text-2xl">{editingTaxon.kind === "group" ? "Editar Grupo" : "Editar Categoría"}</h3>
              <button onClick={() => setEditingTaxon(null)}><X size={22} /></button>
            </div>
            <div className="p-8 space-y-2">
              <label className="overline block mb-2">Nombre</label>
              <input className="aq-input" value={editingTaxon.name || ""}
                onChange={(e) => setEditingTaxon({ ...editingTaxon, name: e.target.value })} data-testid="cat-edit-name" />

              <label className="overline block mb-2 mt-6">Slug (no editable)</label>
              <input className="aq-input bg-[var(--aq-surface)] text-[var(--aq-muted)]" value={editingTaxon.slug || ""} disabled data-testid="cat-edit-slug" />

              <label className="overline block mb-2 mt-6">Descripción</label>
              <textarea className="aq-input min-h-[80px]" value={editingTaxon.description || ""}
                onChange={(e) => setEditingTaxon({ ...editingTaxon, description: e.target.value })} data-testid="cat-edit-description" />

              <label className="overline block mb-2 mt-6">Imagen</label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-[var(--aq-surface)] overflow-hidden flex-shrink-0">
                  {editingTaxon.image_url && <img src={imageUrl(editingTaxon.image_url)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 space-y-2">
                  <input className="aq-input" placeholder="URL o sube un archivo" value={editingTaxon.image_url || ""}
                    onChange={(e) => setEditingTaxon({ ...editingTaxon, image_url: e.target.value })} data-testid="cat-edit-image" />
                  <label className="inline-flex items-center gap-2 cursor-pointer btn-outline">
                    <Upload size={14} /> Subir
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        if (!e.target.files?.[0]) return;
                        try {
                          const path = await uploadSingle(e.target.files[0]);
                          setEditingTaxon((prev) => ({ ...prev, image_url: path }));
                          toast.success("Imagen subida");
                        } catch { toast.error("No se pudo subir la imagen"); }
                      }}
                      data-testid="cat-edit-upload"
                    />
                  </label>
                </div>
              </div>

              <div className="pt-10 flex gap-4">
                <button onClick={saveTaxon} data-testid="cat-edit-save" className="btn-primary flex-1">Guardar</button>
                <button onClick={() => setEditingTaxon(null)} className="btn-outline">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
